import { CATEGORIES, BANNABLE_CATEGORIES } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { LegalDialog } from "@/components/LegalDialog";
import { TagInput } from "@/components/modelform/TagInput";
import { Ban } from "lucide-react";

export const BasicsCard = ({ form, setForm, onToggleCategory }) => (
  <Card className="rounded-2xl">
    <CardHeader className="pb-3"><CardTitle className="text-base">Basics</CardTitle></CardHeader>
    <CardContent className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Model name</Label>
          <Input data-testid="model-form-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl bg-card" />
        </div>
        <div className="space-y-1.5">
          <Label>Gender</Label>
          <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
            <SelectTrigger data-testid="model-form-gender-select" className="rounded-xl bg-card"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="female">Female</SelectItem>
              <SelectItem value="male">Male</SelectItem>
              <SelectItem value="non-binary">Non-binary</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Bio</Label>
        <Textarea data-testid="model-form-bio-input" rows={3} value={form.bio} placeholder="Describe the look, experience, and what makes this model distinctive…"
          onChange={(e) => setForm({ ...form, bio: e.target.value })} className="rounded-xl bg-card" />
      </div>
      <div className="space-y-1.5">
        <Label>Strengths (categories)</Label>
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button key={c.value} type="button" data-testid={`model-form-category-${c.value}`}
              onClick={() => onToggleCategory(c.value)}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-[background-color,border-color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                form.categories.includes(c.value)
                  ? "border-[hsl(174,38%,34%)] bg-[hsl(174,38%,92%)] text-[hsl(174,45%,18%)]"
                  : "bg-card hover:bg-muted"
              }`}>
              {c.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between rounded-xl border bg-card p-3">
        <div>
          <p className="text-sm font-medium">Available for bookings</p>
          <p className="text-[11px] text-muted-foreground">Turn off to hide from the marketplace</p>
        </div>
        <Switch data-testid="model-form-available-switch" checked={form.available} onCheckedChange={(v) => setForm({ ...form, available: v })} />
      </div>
    </CardContent>
  </Card>
);

export const FeaturesCard = ({ form, setForm }) => (
  <Card className="rounded-2xl">
    <CardHeader className="pb-3"><CardTitle className="text-base">Features & measurements</CardTitle></CardHeader>
    <CardContent className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-4">
        <div className="space-y-1.5">
          <Label>Height (cm)</Label>
          <Input data-testid="model-form-height-input" type="number" value={form.features.height_cm}
            onChange={(e) => setForm({ ...form, features: { ...form.features, height_cm: e.target.value } })} className="rounded-xl bg-card" />
        </div>
        <div className="space-y-1.5">
          <Label>Hair colour</Label>
          <Input data-testid="model-form-hair-input" value={form.features.hair_color}
            onChange={(e) => setForm({ ...form, features: { ...form.features, hair_color: e.target.value } })} className="rounded-xl bg-card" />
        </div>
        <div className="space-y-1.5">
          <Label>Eye colour</Label>
          <Input data-testid="model-form-eyes-input" value={form.features.eye_color}
            onChange={(e) => setForm({ ...form, features: { ...form.features, eye_color: e.target.value } })} className="rounded-xl bg-card" />
        </div>
        <div className="space-y-1.5">
          <Label>Skin tone</Label>
          <Input data-testid="model-form-skin-input" value={form.features.skin_tone}
            onChange={(e) => setForm({ ...form, features: { ...form.features, skin_tone: e.target.value } })} className="rounded-xl bg-card" />
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-5">
        {[
          ["bust_cm", "Bust (cm)"], ["waist_cm", "Waist (cm)"], ["hips_cm", "Hips (cm)"], ["shoe_size_eu", "Shoe (EU)"],
        ].map(([key, label]) => (
          <div key={key} className="space-y-1.5">
            <Label>{label}</Label>
            <Input data-testid={`model-form-${key}-input`} type="number" value={form.measurements[key]}
              onChange={(e) => setForm({ ...form, measurements: { ...form.measurements, [key]: e.target.value } })} className="rounded-xl bg-card" />
          </div>
        ))}
        <div className="space-y-1.5">
          <Label>Dress size</Label>
          <Input data-testid="model-form-dress-input" value={form.measurements.dress_size} placeholder="EU 36 / M"
            onChange={(e) => setForm({ ...form, measurements: { ...form.measurements, dress_size: e.target.value } })} className="rounded-xl bg-card" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export const PermissionsCard = ({ form, user, onToggleBanned }) => (
  <Card className="rounded-2xl">
    <CardHeader className="pb-3">
      <CardTitle className="flex items-center gap-2 text-base">
        <Ban className="h-4 w-4 text-destructive" /> Content permissions (opt-out)
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Categories {user?.role === "agency" ? "this model" : "you"} will <span className="font-semibold text-foreground">never</span> work
        in. These bans are technically enforced: the screening engine automatically rejects any client job that
        touches them, before it ever reaches {user?.role === "agency" ? "the model" : "you"}.
      </p>
      <div className="flex flex-wrap gap-2">
        {BANNABLE_CATEGORIES.map((c) => {
          const banned = form.banned_categories.includes(c.value);
          return (
            <button key={c.value} type="button" data-testid={`model-form-ban-${c.value}`}
              onClick={() => onToggleBanned(c.value)}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-[background-color,border-color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                banned
                  ? "border-[hsl(6,62%,55%)] bg-[hsl(6,62%,94%)] text-[hsl(6,55%,25%)] line-through"
                  : "bg-card hover:bg-muted"
              }`}>
              {banned ? "✕ " : ""}{c.label}
            </button>
          );
        })}
      </div>
      {form.banned_categories.length > 0 && (
        <p className="text-[11px] text-muted-foreground">
          Banned: {form.banned_categories.length} categor{form.banned_categories.length === 1 ? "y" : "ies"} — clients cannot book {user?.role === "agency" ? "this model" : "you"} for these under any circumstances.
        </p>
      )}
    </CardContent>
  </Card>
);

export const SkillsRatesCard = ({ form, setForm, user }) => (
  <Card className="rounded-2xl">
    <CardHeader className="pb-3"><CardTitle className="text-base">Skills, interests & rates</CardTitle></CardHeader>
    <CardContent className="space-y-4">
      <TagInput label="Skills" values={form.skills} onChange={(v) => setForm({ ...form, skills: v })} placeholder="e.g. runway walk — press Enter" testId="model-form-skills-input" />
      <TagInput label="Interests" values={form.interests} onChange={(v) => setForm({ ...form, interests: v })} placeholder="e.g. sustainable fashion" testId="model-form-interests-input" />
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Typical rate minimum (USD)</Label>
          <Input data-testid="model-form-rate-min-input" type="number" value={form.rate_min}
            onChange={(e) => setForm({ ...form, rate_min: e.target.value })} className="rounded-xl bg-card" />
        </div>
        <div className="space-y-1.5">
          <Label>Typical rate maximum (USD)</Label>
          <Input data-testid="model-form-rate-max-input" type="number" value={form.rate_max}
            onChange={(e) => setForm({ ...form, rate_max: e.target.value })} className="rounded-xl bg-card" />
        </div>
      </div>
      {user?.role === "agency" && (
        <div className="space-y-1.5">
          <Label>Agency commission (% of each licence fee, 0–50)</Label>
          <Input data-testid="model-form-commission-input" type="number" min="0" max="50" value={form.agency_commission_pct}
            placeholder="e.g. 20"
            onChange={(e) => setForm({ ...form, agency_commission_pct: e.target.value })} className="rounded-xl bg-card" />
          <p className="text-[11px] text-muted-foreground">
            Payouts are split transparently in your wallet ledger: model share vs agency commission.
          </p>
        </div>
      )}
    </CardContent>
  </Card>
);

export const TosCard = ({ user, tosAccepted, setTosAccepted, confirmed18, setConfirmed18 }) => (
  <Card data-testid="model-form-tos-card" className="rounded-2xl border-2 border-[hsl(174,38%,70%)]">
    <CardContent className="space-y-3 p-5">
      <div className="flex items-start gap-3">
        <Checkbox data-testid="model-form-tos-checkbox" id="tos" checked={tosAccepted} onCheckedChange={setTosAccepted} className="mt-0.5" />
        <label htmlFor="tos" className="text-sm leading-snug">
          I accept the{" "}
          <LegalDialog doc="model-terms" trigger={
            <button type="button" data-testid="model-form-tos-link" className="font-semibold text-[hsl(174,38%,30%)] underline">Model Terms of Service</button>
          } />{" "}
          — non-exclusive digital rights (I keep ownership of my identity), binding content opt-outs, the right
          to revoke my likeness at any time, and upfront payment. See also our{" "}
          <LegalDialog doc="privacy" trigger={
            <button type="button" data-testid="model-form-privacy-link" className="font-semibold text-[hsl(174,38%,30%)] underline">Privacy & Data Security Policy</button>
          } />.
        </label>
      </div>
      <div className="flex items-start gap-3">
        <Checkbox data-testid="model-form-18plus-checkbox" id="adult" checked={confirmed18} onCheckedChange={setConfirmed18} className="mt-0.5" />
        <label htmlFor="adult" className="text-sm leading-snug">
          I confirm the model is <span className="font-semibold">at least 18 years old</span>, that I own the
          rights to every photo I upload, and that the photos depict {user?.role === "agency" ? "a model I am contractually authorised to represent" : "me"} —
          uploading someone else's face is identity theft and results in permanent removal.
        </label>
      </div>
    </CardContent>
  </Card>
);
